import { atom, selector } from "recoil";
import { recoilPersist } from "recoil-persist";

import { Category } from "./api/categoryAPI";
import { Post } from "./api/postAPI";
import { Slide } from "./api/slideAPI";

const { persistAtom } = recoilPersist();

export const currentCategoryAtom = atom<Category>({
  key: "currentCategory",
  default: { id: 0, name: "", depth: 0, children: [], posts: [], listOrder: 0 },
  effects_UNSTABLE: [persistAtom],
});

export const postListAtom = atom<Post[]>({
  key: "postList",
  default: [],
  effects_UNSTABLE: [persistAtom],
});

export const currentPostAtom = atom<Post>({
  key: "currentPost",
  default: {
    id: 0,
    categoryId: 0,
    title: "",
    writer: "",
    slides: [],
    isActive: false,
    createdAt: "",
    modifiedAt: "",
    content: "",
    postThumbnail: "",
    comments: [],
    isSecret: false,
  },
  effects_UNSTABLE: [persistAtom],
});

export const initialSlide: Slide = {
  slideId: 0,
  slidename: "",
  filename: "",
  caption: "",
  gender: "",
  age: "",
  site: "",
  zindanName: "",
  thumbnail: "",
  annotations: [],
  slideComments: [],
  description: "",
};

export const currentSlideAtom = atom<Slide>({
  key: "currentSlide",
  default: initialSlide,
  effects_UNSTABLE: [persistAtom],
});

export const categoryViewStateAtom = atom({
  key: "categoryViewStateAtom",
  default: {
    activeKeys: [] as string[],
    scrollY: 0,
  },
});

// NavBar 표시/숨김 상태 (포스트 페이지 등에서 헤더 높이 동적 계산용)
export const navBarVisibleAtom = atom<boolean>({
  key: "navBarVisible",
  default: true,
});

export type ViewerId = "left" | "right";

export const viewerSlidesAtom = atom<Record<ViewerId, Slide | null>>({
  key: "viewerSlidesAtom",
  default: {
    left: null,
    right: null,
  },
});

export type ViewerMediaKind = "slide" | "image" | "gross";

export interface ViewerImage {
  kind: "image" | "gross";
  id: number;
  pathFileName: string;
  annotations: any[] | null;
}

export const viewerImagesAtom = atom<Record<ViewerId, ViewerImage | null>>({
  key: "viewerImagesAtom",
  default: {
    left: null,
    right: null,
  },
});

export const activeViewerIdAtom = atom<ViewerId>({
  key: "activeViewerIdAtom",
  default: "left",
});

export const isDualViewerAtom = atom<boolean>({
  key: "isDualViewerAtom",
  default: false,
});

// annotation 라벨 글씨 배율. 1 = 기본 크기(최소). 모든 뷰어에 공통 적용, 새로고침 후에도 유지
export const ANNOTATION_LABEL_SCALES = [1, 1.25, 1.5, 1.75, 2, 2.5, 3];
export const annotationLabelScaleAtom = atom<number>({
  key: "annotationLabelScaleAtom",
  default: 1,
  effects_UNSTABLE: [persistAtom],
});

// 듀얼 뷰어 줌 동기화
export const isZoomSyncAtom = atom<boolean>({
  key: "isZoomSyncAtom",
  default: false,
  effects_UNSTABLE: [persistAtom],
});

export const activeSlideSelector = selector<Slide | null>({
  key: "activeSlideSelector",
  get: ({ get }) => {
    const slides = get(viewerSlidesAtom);
    const activeId = get(activeViewerIdAtom);
    return slides[activeId];
  },
  set: ({ get, set }, newValue) => {
    const activeId = get(activeViewerIdAtom);
    set(viewerSlidesAtom, (prev) => ({
      ...prev,
      [activeId]: newValue as Slide | null,
    }));
  },
});
